import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import _ from 'lodash';

import {GeneralService} from "../../../../services/generalService";
import {DICTIONARY} from "../../../../dictionary";
import {RouteService} from "../../../../services/routeService";
import {NotificationService} from "../../../../services/notificationService";
import {TrustifiTableComponent} from "../../../uiComponents/trustifi-table/trustifi-table.component";


@Component({
	selector: 'account-compromised-configuration-wizard',
	templateUrl: './account-compromised-configuration-wizard.component.html',
})
export class AccountCompromisedConfigurationWizardComponent implements OnInit {

	@Input() incidentTypes;
	@Input() users;
	@Input() exchangeServerConfig;
	@Input() updateConfigInProcess;
	@Output() closePopupFn: EventEmitter<any> = new EventEmitter<any>();
	@Output() confirmPopupFn: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('usersSelectTable') usersSelectTableEl: TrustifiTableComponent;


	constructor(public gs: GeneralService,
				private rs: RouteService,
				private ns: NotificationService) {
	}

	dic = DICTIONARY;
	_ = _;

	// environment
	step = this.dic.CONSTANTS.exchangeRulesSteps.generalInformation;
	configStages = [];
	currentConfigStage;
	configData;
	usersSelectionTypes = {all: 'all', specific: 'specific'};
	errorMsg = '';
	generateTokenInProcess;
	token;
	inputs;
	disconnected;
	verifyRulesStatus;

	incidentTypesInfo = {
		suspicious_device_change: {
			description: 'This alert is triggered when a user\'s email is opened from a device that hasn\'t been used with the account before. It may indicate unauthorized access if the user hasn\'t recently changed devices.',
			icon: 'suspicious-device.png'
		},
		suspicious_location_change: {
			description: 'This incident is triggered when a user\'s email has been opened from a geographical location in which the user isn\'t usually active. It could suggest a compromised account if the user hasn\'t recently traveled or used a VPN.',
			icon: 'suspicious-location.png'
		},
		sending_unusual_amount_of_emails: {
			description: 'This incident is triggered when there is an unusual increase in email  volume sent from the account, compared to normal behavior. Such activity is often associated with hijacked mailboxes being used to send spam or phishing campaigns.',
			icon: 'unusual-amount.png'
		},
		sending_burst_emails: {
			description: 'Detects sudden spikes of outbound mail going to multiple external domains in a short time window.',
			icon: 'unusual-amount.png'
		},
		sending_sensitive_to_new_domains: {
			description: 'Triggers when sensitive content (like internal documents or personal data) is emailed to domains that haven’t been contacted before. This may indicate data exfiltration by an unauthorized actor.',
			icon: 'to-new-domains.png'
		},
		sending_sensitive_to_personal_email: {
			description: 'Fires when sensitive information is sent to common free email services (e.g., Gmail, Yahoo, Outlook). Attackers often forward stolen data to personal addresses to avoid detection.',
			icon: 'to-personal-email.png'
		},
		email_breach: {
			description: 'Indicates that a user\'s mailbox password has been found in recent darknet data breach collections. If a user\'s mailbox is not protected by MFA or their password has not been recently updated, it may indicate a mailbox breach',
			icon: 'email-breach.png'
		},
		abnormal_user_activity: {
			description: 'Abnormal user activity',
			icon: 'abnormal_user_activity.png'
		},
		new_forward_redirect_rules: {
			description: 'Occurs when new mailbox rules are created that redirect, delete, or hide incoming messages. Attackers often use rules to conceal their activity and maintain access.',
			icon: 'new-forward-redirect.png'
		},
		outbound_malicious_quarantines: {
			description: 'Triggers when an outbound email from a user is quarantined for malicious links or files. Each quarantined email creates a separate incident.',
			icon: 'outbound-malicious-quarantine.png'
		},
		high_recipient_compliant_rate: {
			description: 'Triggered when a high percentage of recipients mark emails from the account as spam or report them. This can reflect phishing attempts or abusive usage from a compromised account.',
			icon: 'high-complaint-rate.png'
		},
		high_recipient_hard_bounce_rate: {
			description: 'Flags when many sent emails fail to reach recipients due to invalid addresses. It may indicate that the account is being used to send bulk or unauthorized messages.',
			icon: 'hard-bounce.png'
		}
	};

	ngOnInit() {

		this.configStages = _.map(_.keys(this.dic.CONSTANTS.accountCompromisedIncidentTypes), incidentTypeKey => ({
			key: incidentTypeKey,
			...this.dic.CONSTANTS.accountCompromisedIncidentTypes[incidentTypeKey] // {name: ..., tooltip: ...}
		}));
		this.currentConfigStage = this.configStages[0];

		this.configData = _.cloneDeep(this.incidentTypes);
	}

	closePopup = () => {
		this.closePopupFn.emit();
	}

	backStep = () => {
		this.errorMsg = '';
		switch (this.step) {
			case this.dic.CONSTANTS.compromisedConfigurationSteps.configuration:
				if (!this.isStepDataValid(this.currentConfigStage.key)) {
					return;
				}

				const currentConfigStageIndex = this.configStages.indexOf(this.currentConfigStage);
				if (currentConfigStageIndex === 0) {
					this.step = this.dic.CONSTANTS.compromisedConfigurationSteps.generalInformation;
				}
				else {
					this.currentConfigStage = this.configStages[currentConfigStageIndex - 1];
				}
				break;

			case this.dic.CONSTANTS.compromisedConfigurationSteps.summary:
				this.step = this.dic.CONSTANTS.compromisedConfigurationSteps.configuration;
				break;

			default:
				return;
		}
	}

	nextStep = () => {
		this.errorMsg = '';

		// reset search
		if (this.usersSelectTableEl) {
			this.usersSelectTableEl.search.text = '';
			this.usersSelectTableEl.searchItem();
		}

		switch (this.step) {
			case this.dic.CONSTANTS.compromisedConfigurationSteps.generalInformation:
				this.step = this.dic.CONSTANTS.compromisedConfigurationSteps.configuration;
				break;

			case this.dic.CONSTANTS.compromisedConfigurationSteps.configuration:
				if (!this.isStepDataValid(this.currentConfigStage.key)) {
					return;
				}

				const currentConfigStageIndex = this.configStages.indexOf(this.currentConfigStage);
				if (currentConfigStageIndex + 1 === this.configStages.length) {
					this.step = this.dic.CONSTANTS.compromisedConfigurationSteps.summary;
				}
				else {
					this.currentConfigStage = this.configStages[currentConfigStageIndex + 1];
				}
				break;
			default:
				return;
		}
	}

	isStepDataValid = (stepKey) => {
		/*if (this.configData[stepKey].selectUsersType === this.usersSelectionTypes.specific && !_.filter(this.configData[stepKey].users, 'selected').length) {
			this.errorMsg = util.format(this.dic.ERRORS.selectAtLeastOneX, 'user');
			return false;
		}*/

		const selectedActions = _.filter(_.values(this.configData[stepKey].actions), 'selected');
		const selectedActionsNames = _.map(selectedActions, 'name');
		const blockAndRiskyOverlap = _.intersection(selectedActionsNames, [this.dic.CONSTANTS.accountCompromisedRulesSubActions.blockUser.name, this.dic.CONSTANTS.accountCompromisedRulesSubActions.blockUser24hours.name, this.dic.CONSTANTS.accountCompromisedRulesSubActions.riskyUser24hours.name]);
		if (blockAndRiskyOverlap.length > 1) {
			this.errorMsg = `"${blockAndRiskyOverlap[0]}" and "${blockAndRiskyOverlap[1]}" cannot be selected together`;
			return false;
		}

		return true;
	}

	searchUser = (event) => {
		this.configData[this.currentConfigStage.key].users.forEach(record => {
			// search
			if (event.searchTerm) {
				const isFound = searchTextExecute(record, event.searchTerm);
				if (!isFound) {
					record.hide = true;
					return;
				}
			}

			record.hide = false;
		});
	}

	updateAccountCompromisedConfig() {
		const data = {
			action: this.dic.CONSTANTS.accountCompromisedConfigActions.allIncidentTypes,
			incidentTypes: []
		};

		this.configStages.forEach(incidentType => {
			// validation
			if (!this.isStepDataValid(incidentType.key)) {
				this.currentConfigStage = incidentType;
				return;
			}
			//

			const enableEmails = _.map(_.filter(this.configData[incidentType.key].users, 'selected'),'email');
			const disableEmails = _.map(_.reject(this.configData[incidentType.key].users, 'selected'), 'email');
			const isApplyAllUsers = this.configData[incidentType.key].selectUsersType === this.usersSelectionTypes.all;
			const actions = _.map(_.filter(this.configData[incidentType.key].actions, 'selected'), 'name');

			const incidentTypeData = {
				type: incidentType.key,
				applyAllUsers: isApplyAllUsers,
				enableEmails: enableEmails,
				disableEmails: disableEmails,
				actions: actions
			};

			data.incidentTypes.push(incidentTypeData);
		});

		this.confirmPopupFn.emit({data});
	}

	getUserEmailRules = () => {
		this.verifyRulesStatus = 'in-process';

		this.rs.verifyUserEmailRulesIntegration({}).then((response) => {
			this.verifyRulesStatus = 'success';
			this.exchangeServerConfig.enabled = true;
			this.exchangeServerConfig.advanced_enabled = true;
		}, () => {
			this.verifyRulesStatus = 'failure';
			this.ns.closeAll();
		});
	};

}

function searchTextExecute(user, searchTerm) {
	searchTerm = searchTerm.toLowerCase();
	return ((user.name && user.name.toLowerCase().indexOf(searchTerm) > -1) ||
			(user.email && user.email.toLowerCase().indexOf(searchTerm) > -1));
}
